import React from 'react';
import PropTypes from 'prop-types';
import useDataSourceContext from '@afosto/app-components-react/DataSource/useDataSourceContext';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@afosto/app-components-react/Icon';
import { users as usersApi } from '@afosto/iam/api/Users';
import translations from './translations';
import styled from 'styled-components';

const ActionButton = styled(IconButton)`
  && {
    &:last-child {
      margin-left: ${props => props.theme.spacing()}px;
    }
  }
`;

const UserInvitesActions = ({ userInvite }) => {
  const intl = useIntl();
  const { reloadDataSource } = useDataSourceContext();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const handleSendNewInvite = async () => {
    try {
      await usersApi.requestToken({
        email: userInvite.email,
        type: 'invite',
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.userInviteSent), {
        variant: 'success',
      });
      reloadDataSource();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.userInviteNotSent), {
        variant: 'error',
      });
    }
  };

  const handleCancelInvites = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmCancelInvite.title),
        description: intl.formatMessage(translations.confirmCancelInvite.description),
        buttons: [
          {
            label: intl.formatMessage(translations.confirmCancelInvite.actions.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmCancelInvite.actions.revoke),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
        catchOnCancel: false,
      });

      if (confirmResponse === 'delete') {
        await usersApi.deleteUser(userInvite.id);

        reloadDataSource();
        enqueueSnackbar(intl.formatMessage(translations.feedback.userInviteCancelled), {
          variant: 'success',
        });
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.userInviteNotCancelled), {
        variant: 'error',
      });
    }
  };

  return (
    <>
      {userInvite?.is_expired && (
        <ActionButton onClick={handleSendNewInvite}>
          <Icon icon={['fal', 'paper-plane']} />
        </ActionButton>
      )}
      <ActionButton onClick={handleCancelInvites}>
        <Icon icon={['fal', 'ban']} />
      </ActionButton>
    </>
  );
};

UserInvitesActions.propTypes = {
  userInvite: PropTypes.object.isRequired,
};

export default UserInvitesActions;
